/**
 * What rescoring one already-scored lead costs, in credits (a positive amount).
 *
 * One number for every place that states or charges it: the list footer's Enrich menu, the
 * "Retrigger Lead Scoring" modal, the API's charge (`CreditPrices.RESCORE_LEAD`), and its Swagger
 * and MCP descriptions. The menu said 0.2 while the modal and the charge said 0.5, each hard-coded
 * on its own; the price is 0.2 (PREC-4290).
 */
export const RESCORE_LEAD_CREDITS = 0.2;

/**
 * What one job-change check of a tracked person costs, in credits (a positive amount): a job-change
 * list's lead, or a Profile Tracker profile once a month.
 *
 * The Profile Tracker's checkbox quoted "1 credit per personal profile per month" and nothing was
 * charged at all; the check costs 0.1, like the list's (PREC-4286). The label interpolates this.
 */
export const JOB_CHANGE_CHECK_CREDITS = 0.1;

/**
 * What one signal scan of a tracked company costs, in credits (a positive amount), before the
 * small per-person surcharge. The Profile Tracker's "Run signal search" confirmation quotes it and
 * the API's `CreditPrices.SIGNAL_SCORING` charges it (PREC-3240).
 */
export const SIGNAL_SCAN_COMPANY_CREDITS = 2;

/**
 * What enriching one person in an uploaded XLSX/CSV costs, in credits (a positive amount), charged
 * per person searched, also when no one is found: the list wizard's "Upload a list of people" and
 * "Upload a list of job titles" cards quote it, and the API's `CreditPrices.NORMAL`
 * (billed as `ENRICH_PERSON_IN_XSLX` for this flow, and as the generic fallback elsewhere)
 * charges it (PREC-4638). The copy names the searched person, not a found one, because the charge
 * is booked from the search's work, not its outcome. `NORMAL` stays its own literal so changing
 * this price does not silently reprice every flow that uses the fallback.
 */
export const PERSON_UPLOAD_CREDITS = 1;

/**
 * What finding a person at one company costs, in credits (a positive amount), whether or not
 * someone is found: the Find person step of a company list, and the automatic search a tracker
 * list runs for a company signal with no known person (PREC-4399). The Find person step's info box
 * and the tracker switch's price line interpolate it, and the API charges it.
 */
export const FIND_PERSON_COMPANY_CREDITS = 1;

/**
 * What a person search at one company costs once it has run, in credits (a positive amount): one
 * {@link FIND_PERSON_COMPANY_CREDITS} per person found, and one for a company where nobody was
 * found (PREC-4398's bulk find person, which can find up to three people per company). The bulk
 * modal quotes it per company and the API charges it.
 */
export function findPersonSearchCredits(peopleFound: number): number {
	return Math.max(1, Math.floor(peopleFound) || 0) * FIND_PERSON_COMPANY_CREDITS;
}

/**
 * What a lead's own email lookup costs, in credits (a positive amount), charged only when it finds
 * an address. A list set to "Try to find email" runs it for every person it adds, the people a bulk
 * find person finds included: the bulk dialog quotes it (PREC-4398) and the API's
 * `CreditPrices.EMAIL` charges it.
 */
export const EMAIL_LOOKUP_CREDITS = 1;

/**
 * What a lead's own phone lookup costs, in credits (a positive amount). It is charged when the
 * lookup starts and refunded when the provider finds no number (`BetterContactResultService`), so
 * the balance has to cover it up front. A list set to "Try to find phone" runs it for every person
 * it adds: the bulk find person dialog and the credit-confirm modal quote it (PREC-4398, PREC-4263)
 * and the API's `CreditPrices.PHONE` charges it.
 */
export const PHONE_LOOKUP_CREDITS = 10;

/**
 * What a company email and phone lookup costs, in credits (a positive amount), charged per lookup
 * the provider answers. A list set to look up company contact information runs it for a person it
 * adds whose company has neither yet: the bulk find person dialog quotes it (PREC-4398) and the
 * API's `CreditPrices.FIND_COMPANY_EMAIL_OR_PHONE` charges it.
 */
export const COMPANY_CONTACT_LOOKUP_CREDITS = 1;

/**
 * What ordering the first scrape of a source (a custom event, "Scraping as a service") costs, in
 * website-scrape credits (a positive amount). The list wizard's source tile quotes it and blocks
 * below it, and the API charges it when the source is created (PREC-3091).
 */
export const INITIAL_SCRAPE_CREDITS = 1;

/**
 * What re-scraping a source that was already delivered costs, in website-scrape credits (a
 * positive amount): half the first scrape (PREC-3091). The re-scrape dialog quotes it and the API
 * checks and charges it. A cancelled request gives back what its own charge row booked, so a
 * change here never alters the refund of a request that is already open (PREC-4456).
 */
export const RESCRAPE_CREDITS = 0.5;

/**
 * What one Deep Research run on one lead costs, in credits (a positive amount), charged when the
 * research is delivered. The Enrich menu, the Deep Research dialog's cost block and the API's
 * `CreditPrices.DEEP_RESEARCH` read it (PREC-4263).
 */
export const DEEP_RESEARCH_LEAD_CREDITS = 1;

/**
 * What an AI column costs per row it writes, in credits (a positive amount), with or without web
 * access. The AI column dialog's cost block and the API's `CreditPrices.AI_COLUMN` /
 * `CreditPrices.AI_COLUMN_ONLINE` read it (PREC-4263).
 */
export const AI_COLUMN_ROW_CREDITS = 1;

/**
 * What a list preview needs per company it searches, in credits (a positive amount): the list
 * builder's "Start preview" confirmation quotes it and the API's preview pre-flight
 * (`ListPreviewService.validateCredits`) requires it (PREC-4263).
 */
export const PREVIEW_COMPANY_CREDITS = 1;

/**
 * What verifying one user-provided email address costs, in credits (a positive amount): an
 * uploaded, CRM-synced, manual or extension address, verified once before its first email. A
 * Signalist-found address is never charged (the finder verified it). The API's
 * `CreditPrices.EMAIL_VERIFICATION` charges it and the Verify now confirmation quotes it (PREC-4265).
 */
export const EMAIL_VERIFICATION_CREDITS = 0.2;
